import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import Draggable from 'react-draggable';
import { ResizableBox } from 'react-resizable';
import TipTapEditor from './TipTapEditor';
import { useTheme } from '../context/ThemeContext';
import { encryptData, decryptData } from '../utils/encryption';
import { useEncryption } from '../context/EncryptionContext';
import { sanitizeNoteHtml } from '../utils/sanitizeHtml';
import { isPrefixCiphertext } from '../utils/encryptionPolicy';
import { summarizeNote } from '../firebase/ai';
import VaultTransitionControl from './VaultTransitionControl';
import 'react-resizable/css/styles.css';
import './NoteModal.css';

const MIN_WIDTH = 600;
const MIN_HEIGHT = 400;
const DEFAULT_SIZE = { width: 800, height: 600 };
const DEFAULT_POSITION = { x: 0, y: 0 };
const MOBILE_BREAKPOINT = 768;
const MODAL_MARGIN = 32;
const createAiRequestId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `ai_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 14)}`;
};

const NoteModal = ({ 
  isOpen, 
  onRequestClose, 
  title: initialTitle, 
  content: initialContent, 
  onSave, 
  categories, 
  selectedCategory, 
  tags: initialTags = [], 
  availableTags = [], 
  onExport,
  noteId,
  createdAt,
  updatedAt, note, onVaultTransitionComplete
}) => {
  const { theme, themes } = useTheme();
  const isLightTheme = themes[theme]?.mode === 'light';
  const { isEncryptionEnabled, encryptionKey, encryptionPolicy, isEncryptionLocked, verifyCloudKeyBackup, hasCloudKey, setShowCloudKeyModal } = useEncryption();
  const [editedTitle, setEditedTitle] = useState('');
  const [editedContent, setEditedContent] = useState('');
  const [selectedCat, setSelectedCat] = useState(selectedCategory || 'Uncategorized');
  const [tagList, setTagList] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const isVaultNote = note?.protection === 'vault';
  const isEncryptedContent = isVaultNote || isPrefixCiphertext(initialContent, encryptionPolicy);
  const [isDecrypting, setIsDecrypting] = useState(isEncryptedContent);
  const [decryptionError, setDecryptionError] = useState(false);
  const [successfulDecryption, setSuccessfulDecryption] = useState(null);
  const [saveError, setSaveError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [summary, setSummary] = useState('');
  const [summaryError, setSummaryError] = useState('');
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [isVaultTransitioning, setIsVaultTransitioning] = useState(false);
  const summaryRequestId = useRef(null);
  const summaryGeneration = useRef(0);
  const [viewport, setViewport] = useState(() => ({ width: window.innerWidth, height: window.innerHeight }));
  const isMobile = viewport.width < MOBILE_BREAKPOINT;
  useEffect(() => {
    const updateViewport = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);
  const isDecryptionPending = isEncryptedContent && (
    isDecrypting ||
    successfulDecryption?.content !== initialContent ||
    successfulDecryption?.key !== encryptionKey
  );
  const isVaultTransitionLocked = Object.prototype.hasOwnProperty.call(note || {}, 'vaultTransition');
  const canSummarize = Boolean(noteId) && !isEncryptedContent && !isVaultNote && !isVaultTransitionLocked;
  
  // Initialize size with saved value or default
  const [size, setSize] = useState(() => {
    // For new notes (no title and no content), always use default size
    if (!initialTitle && !initialContent) {
      return DEFAULT_SIZE;
    }

    try {
      const savedSize = localStorage.getItem('noteModalSize');
      if (savedSize) {
        const parsed = JSON.parse(savedSize);
        return {
          width: Math.max(parsed.width || DEFAULT_SIZE.width, MIN_WIDTH),
          height: Math.max(parsed.height || DEFAULT_SIZE.height, MIN_HEIGHT)
        };
      }
    } catch (e) {
      console.error('Error loading saved modal size:', e);
    }
    return DEFAULT_SIZE;
  });
  
  // Initialize position with saved value or default
  const [position, setPosition] = useState(() => {
    // For new notes (no title and no content), always use center position
    if (!initialTitle && !initialContent) {
      return DEFAULT_POSITION;
    }

    try {
      const savedPosition = localStorage.getItem('noteModalPosition');
      if (savedPosition) {
        const parsed = JSON.parse(savedPosition);
        return {
          x: Number.isFinite(parsed.x) ? parsed.x : 0,
          y: Number.isFinite(parsed.y) ? parsed.y : 0
        };
      }
    } catch (e) {
      console.error('Error loading saved modal position:', e);
    }
    return DEFAULT_POSITION;
  });
  
  const modalRef = useRef(null);

  useEffect(() => {
    let isCurrent = true;
    const loadContent = async () => {
      setDecryptionError(false);
      setIsDecrypting(isEncryptedContent);
      setSaveError(false);
      if (isCurrent) setEditedTitle(initialTitle || '');
      try {
        if (isEncryptedContent) {
          if (isCurrent) {
            setEditedContent('');
            setSuccessfulDecryption(null);
          }
          if (!encryptionKey) {
            throw new Error('Missing encryption key');
          }
          const encryptedContent = initialContent.replace('encrypted:', '');
          const decryptedContent = await decryptData(encryptedContent, encryptionKey);
          if (!decryptedContent) {
            throw new Error('Decryption resulted in empty content');
          }
          if (isCurrent) {
            setEditedContent(decryptedContent);
            setSuccessfulDecryption({ content: initialContent, key: encryptionKey });
          }
        } else {
          if (isCurrent) {
            setEditedContent(initialContent || '');
            setSuccessfulDecryption(null);
          }
        }
      } catch (error) {
        console.error('Error decrypting content:', error);
        if (isCurrent) {
          setEditedContent('');
          setSuccessfulDecryption(null);
          setDecryptionError(true);
        }
      } finally {
        if (isCurrent) setIsDecrypting(false);
      }
      if (isCurrent) {
        setSelectedCat(selectedCategory || 'Uncategorized');
        setTagList(initialTags || []);
      }
    };
    loadContent();
    return () => {
      isCurrent = false;
    };
  }, [initialTitle, initialContent, selectedCategory, initialTags, encryptionKey, isEncryptedContent]);

  useEffect(() => {
    summaryGeneration.current += 1;
    summaryRequestId.current = null;
    setSummary('');
    setSummaryError('');
    setIsSummarizing(false);
  }, [noteId, initialContent]);

  useEffect(() => {
    if (newTag) {
      const suggestions = availableTags.filter(tag => 
        tag.toLowerCase().includes(newTag.toLowerCase()) && !tagList.includes(tag)
      );
      setTagSuggestions(suggestions);
    } else {
      setTagSuggestions([]);
    }
  }, [newTag, availableTags, tagList]);

  const handleTitleFocus = () => {
    if (editedTitle === '') {
      setEditedTitle('');
    }
  };

  const handleSave = async () => {
    if (isEncryptionLocked || isVaultTransitioning || isVaultNote || isDecryptionPending || decryptionError || isSaving) return;

    const finalTitle = editedTitle.trim() === '' ? new Date().toLocaleDateString() : editedTitle;
    setIsSaving(true);
    setSaveError(false);
    try {
      const sanitizedContent = sanitizeNoteHtml(editedContent);
      let finalContent = sanitizedContent;
      if (isEncryptionEnabled && encryptionKey) {
        const encryptedContent = await encryptData(sanitizedContent, encryptionKey);
        finalContent = `encrypted:${encryptedContent}`;
      }
      if (note?.protection === 'standard') await onSave(finalTitle, finalContent, selectedCat, tagList, 'standard');
      else await onSave(finalTitle, finalContent, selectedCat, tagList);
      onRequestClose();
    } catch (error) {
      console.error('Error saving note:', error);
      setSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleContentChange = (newContent) => {
    setEditedContent(newContent);
  };

  const handleSummarize = async () => {
    if (!canSummarize || isSummarizing) return;

    const requestGeneration = summaryGeneration.current;
    setIsSummarizing(true);
    setSummaryError('');
    try {
      if (!summaryRequestId.current) summaryRequestId.current = createAiRequestId();
      const result = await summarizeNote(noteId, summaryRequestId.current);
      if (summaryGeneration.current === requestGeneration) {
        setSummary(result.summary);
        summaryRequestId.current = null;
      }
    } catch (error) {
      console.error('Unable to create AI summary:', error);
      if (summaryGeneration.current === requestGeneration) {
        if (error?.code === 'functions/aborted') {
          summaryRequestId.current = null;
          setSummaryError('Unable to create the summary. Please try again.');
        } else if (error?.code === 'functions/already-exists') {
          summaryRequestId.current = null;
          setSummaryError('The previous attempt may have been counted, but its result was not received. Click Summarize with AI again to start a new request.');
        } else {
          setSummaryError('Unable to confirm the summary. Please try again later.');
        }
      }
    } finally {
      if (summaryGeneration.current === requestGeneration) setIsSummarizing(false);
    }
  };

  const addTag = (tag) => {
    if (tag && !tagList.includes(tag)) {
      const updatedTags = [...tagList, tag];
      setTagList(updatedTags);
      setNewTag('');
      setTagSuggestions([]);
    }
  };

  const removeTag = (tagToRemove) => {
    setTagList(tagList.filter(tag => tag !== tagToRemove));
  };

  const handleExportCurrentNote = (format) => {
    if (!isVaultNote && onExport) {
      const currentNote = {
        id: Date.now().toString(),
        title: editedTitle,
        content: editedContent,
        category: selectedCat,
        tags: tagList
      };
      onExport(format, [currentNote], encryptionPolicy);
    }
  };

  const handleResize = (event, { size: newSize }) => {
    if (isMobile) return;
    const nextSize = {
      width: Math.max(newSize.width, MIN_WIDTH),
      height: Math.max(newSize.height, MIN_HEIGHT)
    };
    setSize(nextSize);
    try {
      localStorage.setItem('noteModalSize', JSON.stringify(nextSize));
    } catch (e) {
      console.error('Error saving modal size:', e);
    }
  };

  const handleDragStop = (e, data) => {
    if (isMobile) return;
    const nextPosition = { x: data.x, y: data.y };
    setPosition(nextPosition);
    try {
      localStorage.setItem('noteModalPosition', JSON.stringify(nextPosition));
    } catch (error) {
      console.error('Error saving modal position:', error);
    }
  };

  const displayedSize = isMobile
    ? { width: Math.max(0, viewport.width - MODAL_MARGIN), height: Math.max(0, Math.min(DEFAULT_SIZE.height, viewport.height - MODAL_MARGIN)) }
    : size;
  const displayedPosition = isMobile ? DEFAULT_POSITION : {
    x: Math.min(Math.max(position.x, 0), Math.max(0, viewport.width - MIN_WIDTH)),
    y: Math.min(Math.max(position.y, 0), Math.max(0, viewport.height - MIN_HEIGHT))
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Draggable
        handle=".modal-drag-handle"
        bounds="parent"
        disabled={isMobile}
        position={displayedPosition}
        onStop={handleDragStop}
      >
        <ResizableBox
          width={displayedSize.width}
          height={displayedSize.height}
          minConstraints={isMobile ? [0, 0] : [MIN_WIDTH, MIN_HEIGHT]}
          maxConstraints={isMobile ? [displayedSize.width, displayedSize.height] : [viewport.width - 40, viewport.height - 40]}
          onResize={handleResize}
          resizeHandles={isMobile ? [] : ['se']}
          className={`note-modal-dialog ${isLightTheme ? 'bg-white' : 'bg-gray-800'} rounded-lg shadow-xl overflow-hidden`}
          ref={modalRef}
        >
          <div className="h-full flex flex-col">
            <div className={`modal-drag-handle p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center ${isMobile ? '' : 'cursor-move'}`}>
              <div className="flex flex-col min-w-0">
                <h2 className={`text-xl font-bold ${
                  isLightTheme ? 'text-gray-800' : 'text-white'
                }`}>
                  {initialTitle ? 'Edit Note' : 'Create Note'}
                </h2>
                {createdAt && (
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Created: {createdAt.toDate().toLocaleString()}
                    {updatedAt && updatedAt.toDate() > createdAt.toDate() && (
                      <span className="block md:inline md:ml-4">
                        Updated: {updatedAt.toDate().toLocaleString()}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={onRequestClose}
                disabled={isVaultTransitioning}
                aria-label="Close note"
                className="flex-none ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto p-4">
              <div className="space-y-4">
                <VaultTransitionControl note={note} encryptionKey={encryptionKey} encryptionPolicy={encryptionPolicy} isEncryptionEnabled={isEncryptionEnabled} isEncryptionLocked={isEncryptionLocked} isContentReady={!isDecryptionPending && !decryptionError} verifyCloudKeyBackup={verifyCloudKeyBackup} onTransitionStateChange={setIsVaultTransitioning} onComplete={onVaultTransitionComplete} />
                <div>
                  <input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    onFocus={handleTitleFocus}
                    placeholder="Note Title"
                    className={`w-full p-2 border rounded ${
                      isLightTheme
                        ? 'bg-white border-gray-300 text-gray-800'
                        : 'bg-gray-700 border-gray-600 text-white'
                    }`}
                  />
                </div>

                <div>
                  {isDecryptionPending || decryptionError ? (
                    <div className="p-4 text-gray-500 dark:text-gray-400" aria-live="polite">
                      {decryptionError ? 'Encrypted content is unavailable.' : 'Decrypting note…'}
                    </div>
                  ) : (
                    <TipTapEditor
                      content={editedContent}
                      onChange={handleContentChange}
                      onExport={isVaultNote ? undefined : (format) => handleExportCurrentNote(format)}
                    />
                  )}
                </div>

                <div>
                  <select
                    value={selectedCat}
                    onChange={(e) => setSelectedCat(e.target.value)}
                    className={`w-full p-2 border rounded ${
                      isLightTheme
                        ? 'bg-white border-gray-300 text-gray-800'
                        : 'bg-gray-700 border-gray-600 text-white'
                    }`}
                  >
                    {categories
                      .filter(category => category.name !== 'All Notes')
                      .map((category, index) => (
                        <option key={index} value={category.name}>
                          {category.name}
                        </option>
                      ))}
                    <option value="Uncategorized">Uncategorized</option>
                  </select>
                </div>

                <div>
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addTag(newTag)}
                    placeholder="Add a tag and press Enter"
                    className={`w-full p-2 border rounded ${
                      isLightTheme
                        ? 'bg-white border-gray-300 text-gray-800'
                        : 'bg-gray-700 border-gray-600 text-white'
                    }`}
                  />
                  <div className="relative">
                    {tagSuggestions.length > 0 && (
                      <ul className="absolute z-10 bg-white border border-gray-300 rounded-md shadow-md max-h-48 overflow-y-auto mt-1 w-64">
                        {tagSuggestions.map((suggestion, index) => (
                          <li 
                            key={index} 
                            onClick={() => addTag(suggestion)}
                            className="cursor-pointer hover:bg-gray-100 p-2"
                          >
                            {suggestion}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {tagList.map((tag, index) => (
                      <span key={index} className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full">
                        {tag}
                        <button onClick={() => removeTag(tag)} className="ml-2 text-red-500">×</button>
                      </span>
                    ))}
                  </div>
                </div>

                {canSummarize && (
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={handleSummarize}
                      disabled={isSummarizing}
                      className="rounded bg-violet-600 px-4 py-2 text-white hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-opacity-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isSummarizing ? 'Summarizing…' : 'Summarize with AI'}
                    </button>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      AI sends the last saved, unencrypted version of this note to OpenAI. Unsaved changes are not included.
                    </p>
                    {summaryError && (
                      <div role="alert" className="text-red-600 dark:text-red-400">
                        {summaryError}
                      </div>
                    )}
                    {summary && (
                      <div role="status" className="rounded border border-gray-200 p-3 text-gray-800 dark:border-gray-600 dark:text-gray-100">
                        {summary}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end space-x-2">
                  {decryptionError && (
                    <div role="alert" className="mr-auto text-red-600 dark:text-red-400">
                      <div>Unable to decrypt note content. Saving is disabled.</div>
                      {isVaultNote && !encryptionKey && hasCloudKey && (
                        <button
                          type="button"
                          onClick={() => setShowCloudKeyModal(true)}
                          className="mt-2 rounded border border-red-600 px-3 py-1 text-sm hover:bg-red-50 dark:hover:bg-gray-700"
                        >
                          Unlock Vault on this browser
                        </button>
                      )}
                    </div>
                  )}
                  {saveError && (
                    <div role="alert" className="mr-auto text-red-600 dark:text-red-400">
                      Unable to save note. Please try again.
                    </div>
                  )}
                  {isEncryptionLocked && (
                    <div role="status" className="mr-auto text-gray-600 dark:text-gray-300">
                      Note changes are temporarily unavailable while encryption is being verified or changed.
                    </div>
                  )}
                  <button
                    onClick={onRequestClose}
                    disabled={isVaultTransitioning}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100
                      dark:border-gray-600 dark:hover:bg-gray-700 dark:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isEncryptionLocked || isVaultTransitioning || isVaultNote || isDecryptionPending || decryptionError || isSaving}
                    className="note-save-button px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </ResizableBox>
      </Draggable>
    </div>
  );
};

export default NoteModal;
